'use client';

import appLogger from '@/utils/logger';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { useProtectedLayoutContext } from '@/components/layouts/ProtectedLayout/context/ProtectedLayoutProvider';
import { cn } from '@/lib/utils';
import { Building2, ChevronDown, Settings, Shield, Search, X } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { FC, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import CreateWorkspaceModal from './CreateWorkspaceModal';
import TrialWorkspaceIndicator from '../../TrialWorkspaceIndicator';
import { WorkspaceMenuItem } from './WorkspaceMenuItem';
import { useWorkspaceSubscriptionsOptimized } from '@/hooks/useSubscription';
import { WORKSPACE_STORAGE_KEY } from '@/constants/workspace';
import { SIDEBAR_NAV_TYPO } from '@/components/shared/HubTable/tokens';

interface IWorkspaceSwitcherProps {
  className?: string;
  align?: 'start' | 'center' | 'end';
  compact?: boolean;
  navigation?: boolean;
}

// Helper to get display name with fallback (accepts any workspace-like object)
const getWorkspaceDisplayName = (workspace: { workspace_name?: string; vendorname?: string; vendorid: number } | null | undefined) => {
  if (!workspace) return 'No workspace';
  return workspace.workspace_name || workspace.vendorname || `Workspace ${workspace.vendorid}`;
};

const WorkspaceSwitcher: FC<IWorkspaceSwitcherProps> = ({
  className,
  align = 'start',
  compact = false,
  navigation = false,
}) => {
  const router = useRouter();
  const workspaceContext = useWorkspace();
  const layoutContext = useProtectedLayoutContext();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [now] = useState(() => Date.now());

  // Notify layout when dropdown opens/closes to prevent sidebar collapse
  useEffect(() => {
    if (layoutContext?.setIsWorkspaceSwitcherOpen) {
      layoutContext.setIsWorkspaceSwitcherOpen(dropdownOpen);
    }
  }, [dropdownOpen, layoutContext]);

  // Clear search when dropdown closes
  useEffect(() => {
    if (!dropdownOpen) {
      setSearchQuery('');
    }
  }, [dropdownOpen]);

  const activeWorkspaceIdParam = workspaceContext?.activeWorkspace?.vendorid;

  // Get subscription data for active workspace
  const { data: subscriptionsData } = useWorkspaceSubscriptionsOptimized({ workspaceId: activeWorkspaceIdParam });

  // Derived trial data
  const trialWorkspaceData = useMemo(() => {
    const activeWorkspace = workspaceContext?.activeWorkspace;
    
    if (!subscriptionsData?.subscriptions || !activeWorkspace) {
      return null;
    }

    const trialSubscription = subscriptionsData.subscriptions.find(
      (sub) => sub.status === 'trial'
    );

    if (trialSubscription && trialSubscription.current_period_end) {
      const trialEndDate = new Date(trialSubscription.current_period_end);
      const daysRemaining = Math.ceil((trialEndDate.getTime() - now) / (1000 * 60 * 60 * 24));

      return {
        workspaceId: activeWorkspace.vendorid,
        creditsRemaining: 0,
        daysRemaining: Math.max(0, daysRemaining),
      };
    }
    return null;
  }, [subscriptionsData?.subscriptions, workspaceContext?.activeWorkspace, now]);

  // Derived state and context values (must be before any early returns)
  const activeWorkspace = workspaceContext?.activeWorkspace;
  const workspaces = workspaceContext?.workspaces ?? [];
  const switchWorkspace = workspaceContext?.switchWorkspace;
  const isLoading = workspaceContext?.isLoading ?? false;
  const isCreateModalOpen = workspaceContext?.isCreateModalOpen ?? false;
  const setIsCreateModalOpen = workspaceContext?.setIsCreateModalOpen ?? (() => {});
  const isSwitchingWorkspace = workspaceContext?.isSwitchingWorkspace ?? false;

  const getActiveWorkspaceId = useCallback(() => {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem(WORKSPACE_STORAGE_KEY);
    if (!stored || stored === 'null' || stored === 'undefined') return null;
    return Number(stored);
  }, []);

  const activeWorkspaceId = activeWorkspace?.vendorid || getActiveWorkspaceId() || null;

  const { filteredWorkspaces, groupedWorkspaces } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const filtered = workspaces.filter((workspace) =>
      getWorkspaceDisplayName(workspace).toLowerCase().includes(query)
    );

    const memberWorkspaces = filtered.filter(w => w.is_member !== false);
    const superAdminWorkspaces = filtered.filter(w => w.is_member === false);

    return {
      filteredWorkspaces: filtered,
      groupedWorkspaces: {
        member: memberWorkspaces,
        superAdmin: superAdminWorkspaces,
      },
    };
  }, [workspaces, searchQuery]);

  const handleWorkspaceSwitch = useCallback((targetWorkspaceId: number) => {
    if (targetWorkspaceId === activeWorkspaceId) {
      return;
    }
    setDropdownOpen(false);
    switchWorkspace?.(targetWorkspaceId);
  }, [activeWorkspaceId, switchWorkspace]);

  // The navigation variant keeps the same switching and access states in one compact row.
  if (navigation && (!workspaceContext || isLoading || isSwitchingWorkspace || !activeWorkspace)) {
    return <div className='mbn-workspace-status' role='status' title={!activeWorkspace && !isLoading ? 'Contact administrator for access' : undefined}>
      {isLoading ? <span className='mbn-skeleton' aria-label='Loading workspace' /> : isSwitchingWorkspace ? 'Switching…' : 'No workspace'}
    </div>;
  }

  // Early return for missing context (unauthenticated)
  if (!workspaceContext) {
    return (
      <div className={cn('flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200', className)}>
        <Building2 className='h-4 w-4 text-gray-400' />
        <span className='text-sm text-gray-500'>No workspace</span>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200', className)}>
        <div className='animate-pulse flex items-center gap-2'>
          <div className='p-1.5 bg-gray-200 rounded-lg'>
            <Building2 className='h-4 w-4 text-gray-400' />
          </div>
          <div className='flex flex-col gap-1'>
            <div className='h-4 w-24 bg-gray-200 rounded animate-pulse'></div>
            <div className='h-3 w-16 bg-gray-200 rounded animate-pulse'></div>
          </div>
        </div>
      </div>
    );
  }

  // Switching workspace state
  if (isSwitchingWorkspace) {
    if (compact) {
      return (
        <div className={cn('flex items-center justify-center w-full h-11 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg', className)}>
          <div className='p-1.5 bg-blue-100 rounded-lg'>
            <div className='animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full' />
          </div>
        </div>
      );
    }

    return (
      <div className={cn('flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200', className)}>
        <div className='p-1.5 bg-blue-100 rounded-lg'>
          <div className='animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full' />
        </div>
        <div className='flex flex-col'>
          <span className='text-sm font-medium text-blue-700'>Switching...</span>
          <span className='text-xs text-blue-600'>Please wait</span>
        </div>
      </div>
    );
  }

  // No workspace state
  if (!activeWorkspace) {
    if (compact) {
      return (
        <div className={cn('w-full h-11 p-2 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center', className)} title='No Workspace Available'>
          <div className='p-1.5 bg-gray-100 rounded-lg'>
            <Building2 className='h-4 w-4 text-gray-500' />
          </div>
        </div>
      );
    }

    return (
      <div className={cn('w-full p-3 bg-gray-50 border border-gray-200 rounded-lg', className)}>
        <div className='flex items-center gap-2'>
          <div className='p-1.5 bg-gray-100 rounded-lg'>
            <Building2 className='h-4 w-4 text-gray-500' />
          </div>
          <div className='flex flex-col items-start'>
            <span className='text-sm font-medium text-gray-700'>No Workspace</span>
            <span className='text-xs text-gray-500'>Contact administrator for access</span>
          </div>
        </div>
      </div>
    );
  }

  // Main rendering (Compact/Full)
  const commonTrigger = (
    <div className='p-1.5 bg-blue-100 rounded-lg relative'>
      <Building2 className='h-4 w-4 text-blue-600 shrink-0' />
      {trialWorkspaceData?.workspaceId === activeWorkspaceId && trialWorkspaceData && (
        <TrialWorkspaceIndicator
          isTrialActive={true}
          daysRemaining={trialWorkspaceData.daysRemaining}
          creditsRemaining={trialWorkspaceData.creditsRemaining}
          variant="icon"
          className="absolute -top-1 -right-1"
          showCredits={true}
        />
      )}
    </div>
  );

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant='outline'
            disabled={isSwitchingWorkspace}
            className={cn(
              navigation ? 'mbn-workspace-trigger' : compact
                ? 'w-full h-11 p-2 bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 hover:to-gray-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center'
                : 'w-full justify-between gap-2 text-left font-normal bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 hover:to-gray-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 min-h-[44px] px-3 py-2',
              isSwitchingWorkspace && 'opacity-50 cursor-not-allowed',
              className,
            )}
            title={compact || navigation ? getWorkspaceDisplayName(activeWorkspace) : undefined}
            aria-label={`Switch workspace: ${getWorkspaceDisplayName(activeWorkspace)}`}
          >
            <div className='flex items-center gap-3 truncate'>
              {!navigation && commonTrigger}
              {!compact && (
                <div className='flex flex-col truncate'>
                  <div className='flex items-center gap-2'>
                    <span className={cn('truncate', navigation ? 'mbn-workspace-name' : SIDEBAR_NAV_TYPO.workspaceName)}>
                      {getWorkspaceDisplayName(activeWorkspace)}
                    </span>
                    {!navigation && trialWorkspaceData?.workspaceId === activeWorkspaceId && trialWorkspaceData && (
                      <TrialWorkspaceIndicator
                        isTrialActive={true}
                        daysRemaining={trialWorkspaceData.daysRemaining}
                        creditsRemaining={trialWorkspaceData.creditsRemaining}
                        variant="badge"
                        showCredits={false}
                      />
                    )}
                  </div>
                  {!navigation && <span className='text-xs text-slate-500'>
                    {activeWorkspace.member_count} members
                  </span>}
                </div>
              )}
            </div>
            {!compact && <ChevronDown className='h-4 w-4 shrink-0 text-gray-400' />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className={cn('w-[calc(100vw-32px)] sm:w-80 p-0 max-h-[80vh] flex flex-col', navigation && 'mbn mbn-workspace-menu')}>
          {/* Search Bar */}
          <div className='mbn-workspace-search sticky top-0 bg-white z-20 p-3 border-b'>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400' />
              <input
                ref={searchInputRef}
                type='text'
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder='Search workspaces...'
                aria-label='Search workspaces'
                className='w-full pl-9 pr-9 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              />
              {searchQuery && (
                <button type='button' aria-label='Clear workspace search' onClick={() => setSearchQuery('')} className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'>
                  <X className='h-4 w-4' />
                </button>
              )}
            </div>
          </div>

          {/* Workspace List */}
          <div className='overflow-y-auto flex-1 p-2'>
            {filteredWorkspaces.length === 0 ? (
              <div className='px-3 py-8 text-center'>
                <Building2 className='h-8 w-8 text-gray-300 mx-auto mb-2' />
                <p className='text-sm text-gray-500'>No workspaces found</p>
              </div>
            ) : (
              <>
                {groupedWorkspaces.member.length > 0 && (
                  <>
                    <DropdownMenuLabel className='text-xs font-semibold text-gray-500 uppercase tracking-wider px-2 py-1'>My Workspaces</DropdownMenuLabel>
                    {groupedWorkspaces.member.map((workspace) => (
                      <WorkspaceMenuItem
                        key={workspace.vendorid}
                        workspace={workspace}
                        navigation={navigation}
                        isActive={workspace.vendorid === activeWorkspaceId}
                        isSuperAdmin={workspace.is_member === false}
                        isTrialWorkspace={trialWorkspaceData?.workspaceId === workspace.vendorid}
                        trialData={trialWorkspaceData?.workspaceId === workspace.vendorid ? trialWorkspaceData : undefined}
                        onSwitch={handleWorkspaceSwitch}
                        isSwitching={isSwitchingWorkspace}
                      />
                    ))}
                  </>
                )}
                {groupedWorkspaces.superAdmin.length > 0 && (
                  <>
                    {groupedWorkspaces.member.length > 0 && <DropdownMenuSeparator className='my-2' />}
                    <DropdownMenuLabel className='text-xs font-semibold text-purple-600 uppercase tracking-wider px-2 py-1 flex items-center gap-1.5'>
                      <Shield className='h-3 w-3' /> Super Admin Access
                    </DropdownMenuLabel>
                    {groupedWorkspaces.superAdmin.map((workspace) => (
                      <WorkspaceMenuItem
                        key={workspace.vendorid}
                        workspace={workspace}
                        navigation={navigation}
                        isActive={workspace.vendorid === activeWorkspaceId}
                        isSuperAdmin={true}
                        isTrialWorkspace={trialWorkspaceData?.workspaceId === workspace.vendorid}
                        trialData={trialWorkspaceData?.workspaceId === workspace.vendorid ? trialWorkspaceData : undefined}
                        onSwitch={handleWorkspaceSwitch}
                        isSwitching={isSwitchingWorkspace}
                      />
                    ))}
                  </>
                )}
              </>
            )}
          </div>

          <div className='mbn-workspace-footer sticky bottom-0 bg-white border-t p-2'>
            <DropdownMenuItem onClick={() => router.push('/settings?tab=workspace')} className='cursor-pointer rounded-lg p-2 hover:bg-gray-50 transition-colors'>
              <Settings className='mr-2 h-4 w-4' /> Workspace Settings
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateWorkspaceModal
        open={isCreateModalOpen}
        isFirstWorkspace={workspaces.length === 0}
        onOpenChange={(open) => {
          if (workspaces.length === 0 && !open) return;
          setIsCreateModalOpen(open);
        }}
      />
    </>
  );
};

export default WorkspaceSwitcher;
